/**
 * Tidal HEIGHT model — the height half of the shared tidal-reference core, the
 * sibling of the stream model. Source: the RYA eNavigation training dataset, the
 * same place the streams came from: `GET /includes/tideheights/{port}Almanac.json`
 * gives the full training year of High and Low Waters per port (time + height, in
 * the port's own time zone). The feed has NO curve data, so the shape of the rise
 * and fall comes from the printed Admiralty tidal-curve diagrams in the RYA
 * Training Almanac.
 *
 * THE ADMIRALTY CURVE METHOD (what we do between HW and LW):
 *   - Each standard port has a printed "Mean Spring and Neap curves" diagram: a
 *     factor (1.0 at HW down to 0.0 at LW) plotted against hours-from-HW, with a
 *     spring curve and a neap curve.
 *   - For a moment between a HW and the adjacent LW we work out the hours from
 *     HW, read the factor off the curve, then height = LW + factor x (HW - LW).
 *     The day's range (HW - LW) is the real one, so spring/neap and the diurnal
 *     inequality scale themselves.
 *   - Which curve to read: we interpolate between the spring and neap curves by
 *     where the day's range sits between the port's MEAN spring and MEAN neap
 *     ranges (both printed in the diagram's legend).
 *
 * CURVE DATA (digitised, gitignored, local like the height data) lives in
 * `src/tides/heights/curves.json`: per-port `spring`/`neap` factor arrays
 * (0..6 h, 0.25 h steps) plus `springRangeM`/`neapRangeM`. All seven standard
 * ports' diagrams were digitised; they are the same standard semidiurnal shape
 * within digitisation accuracy and each draws its spring and neap curves
 * essentially coincident, so the neap array equals the spring array. Six of the
 * seven standard ports (Victoria, Colville, Port Fraser, Port Fitzroy, Namley
 * Harbour, and Hamilton Sound) carry their own digitised curve; Dunbarton shares
 * the verified standard shape with its own mean ranges. The secondary ports read
 * their rise and fall off their reference standard port's curve, which is the
 * standard RYA practice for a secondary port.
 *
 * Height data lives in `src/tides/heights/<port>.json`: `{port, tzH,
 * events:[[mmdd,hhmm,'H'|'L',heightM], ...]}`. Times are the port's local clock;
 * tzH is its offset from UT (Victoria 0, the southern ports -1), lifted onto the
 * same UTC timeline the scrubber and the stream model use (notional year 2019).
 */
import victoriaRaw from './heights/victoria.json';
import dunbartonRaw from './heights/dunbarton.json';
import portfraserRaw from './heights/portfraser.json';
import namleyRaw from './heights/namley.json';
import hamiltonRaw from './heights/hamilton.json';
import portfitzroyRaw from './heights/portfitzroy.json';
import colvilleRaw from './heights/colville.json';
import edenfieldRaw from './heights/edenfield.json';
import sweetwaterRaw from './heights/sweetwater.json';
import sandquayRaw from './heights/sandquay.json';
import blackmillRaw from './heights/blackmill.json';
import curvesRaw from './heights/curves.json';
import secondariesRaw from './heights/secondaries.json';
import { rhumbDistanceNm } from '@/nav/geodesy';

const YEAR = 2019;

type RawEvent = [number, number, string, number];
interface RawPort {
  port: string;
  tzH: number;
  events: RawEvent[];
}

/** One digitised curve pair + the port's mean ranges (from curves.json). */
interface PortCurve {
  name: string;
  springRangeM: number;
  neapRangeM: number;
  /** factor 1.0 (HW) .. 0.0 (LW) at HOURS[i] from HW; neap == spring here. */
  spring: number[];
  neap: number[];
}
interface CurveFile {
  hoursStep: number;
  hours: number[];
  standard: { spring: number[]; neap: number[] };
  ports: Record<string, { name: string; springRangeM: number; neapRangeM: number; spring?: number[]; neap?: number[] }>;
}
const CURVES = curvesRaw as CurveFile;

export type TideType = 'H' | 'L';

export interface HeightEvent {
  /** UTC epoch milliseconds. */
  t: number;
  type: TideType;
  /** Height above chart datum, metres. */
  heightM: number;
}

export interface HeightPort {
  id: string;
  name: string;
  /** Time-zone offset of the published times from UT, hours (e.g. -1). */
  tzH: number;
  /** HW/LW events for the training year, sorted in time. */
  events: HeightEvent[];
  /** Smallest and largest consecutive HW<->LW range (fallback spring/neap read). */
  minRangeM: number;
  maxRangeM: number;
  /**
   * The digitised tidal curve + mean ranges for this port. `ownCurve` is false
   * when we fell back to the shared standard shape (no diagram of this port's own
   * was digitised, or no per-port curve is stored yet).
   */
  curve: PortCurve;
  ownCurve: boolean;
  /** For a secondary port, the standard port its HW/LW are derived from. */
  secondaryOf?: string;
  /**
   * Charted position [lon, lat] of the port, so the picker can order ports by
   * distance to the boat (or the chart centre). Undefined only for a port whose
   * position we can't honestly derive from the chart.
   */
  position?: [number, number];
}

/**
 * Charted lat/long for each training port, [lon, lat]. These are read straight
 * off the RYA training chart cells in public/charts (GB3RYA30, the whole-area
 * Chart 3), taking the centroid of the charted feature that carries the port's
 * own name: the built-up area for a town (Victoria, Dunbarton, Port Fraser,
 * Port Fitzroy, Edenfield, Blackmill), the named sea/harbour area for a harbour
 * or sound (Namley Harbour, Hamilton Sound, Sweetwater Bay, Sandquay Bay), and
 * the charted marina for Colville, which has no built-up-area polygon. Nothing
 * here is invented: every position comes from the chart's own geometry, so the
 * proximity ordering is honest for the fictional training area.
 */
const PORT_POSITIONS: Record<string, [number, number]> = {
  victoria: [-6.20361, 46.46248],
  dunbarton: [-5.98316, 46.19702],
  portfraser: [-5.96922, 46.41732],
  namleyharbour: [-5.75935, 46.44249],
  portfitzroy: [-5.88716, 45.63966],
  hamiltonsound: [-6.18192, 45.62855],
  colville: [-6.14451, 45.94964],
  edenfield: [-6.12014, 45.71982],
  sweetwater: [-6.17212, 45.85739],
  sandquay: [-6.07838, 45.88224],
  blackmill: [-5.79576, 45.62419],
};

/** Resolve the curve for a port id: its own digitised curve, else the standard. */
function curveFor(id: string): { curve: PortCurve; own: boolean } {
  const p = CURVES.ports[id];
  if (p && p.spring && p.neap) {
    return { curve: { name: p.name, springRangeM: p.springRangeM, neapRangeM: p.neapRangeM, spring: p.spring, neap: p.neap }, own: true };
  }
  // No per-port curve stored: share the verified standard shape, keep this
  // port's own mean ranges if we have them so the spring/neap blend stays honest.
  const name = p?.name ?? id;
  const springRangeM = p?.springRangeM ?? 0;
  const neapRangeM = p?.neapRangeM ?? 0;
  return { curve: { name, springRangeM, neapRangeM, spring: CURVES.standard.spring, neap: CURVES.standard.neap }, own: false };
}

/** Lift the compact raw events onto the UTC timeline (handles the Dec lead-in). */
function eventsFromRaw(raw: RawPort): HeightEvent[] {
  const out: HeightEvent[] = [];
  let seenJan = false;
  for (const [mmdd, hhmm, type, h] of raw.events) {
    const mo = Math.floor(mmdd / 100);
    const day = mmdd % 100;
    if (mo === 1) seenJan = true;
    const year = mo === 12 && !seenJan ? YEAR - 1 : YEAR;
    const localMs = Date.UTC(year, mo - 1, day, Math.floor(hhmm / 100), hhmm % 100);
    out.push({ t: localMs - raw.tzH * 3_600_000, type: type as TideType, heightM: h });
  }
  out.sort((a, b) => a.t - b.t);
  return out;
}

function buildPort(raw: RawPort): HeightPort {
  const events = eventsFromRaw(raw);
  let minRangeM = Infinity;
  let maxRangeM = 0;
  for (let i = 0; i + 1 < events.length; i++) {
    const r = Math.abs(events[i + 1].heightM - events[i].heightM);
    if (r < minRangeM) minRangeM = r;
    if (r > maxRangeM) maxRangeM = r;
  }
  const id = raw.port.toLowerCase().replace(/\s+/g, '');
  const { curve, own } = curveFor(id);
  return {
    id,
    name: raw.port,
    tzH: raw.tzH,
    events,
    minRangeM: Number.isFinite(minRangeM) ? minRangeM : 0,
    maxRangeM,
    curve,
    ownCurve: own,
    position: PORT_POSITIONS[id],
  };
}

export const HEIGHT_PORTS: HeightPort[] = [
  buildPort(victoriaRaw as RawPort),
  buildPort(dunbartonRaw as RawPort),
  buildPort(portfraserRaw as RawPort),
  buildPort(namleyRaw as RawPort),
  buildPort(portfitzroyRaw as RawPort),
  buildPort(hamiltonRaw as RawPort),
  buildPort(colvilleRaw as RawPort),
];

export const DEFAULT_HEIGHT_PORT_ID = 'victoria';

// --- Secondary ports (classic differences method) ---------------------------
interface SecondaryDef {
  name: string;
  ref: string;
  tzH: number;
  hwTimeDiffMin: number;
  lwTimeDiffMin: number;
  hwSpringDiffM: number;
  hwNeapDiffM: number;
  lwSpringDiffM: number;
  lwNeapDiffM: number;
}

/** Mean range of the cycle around event i (to read its spring/neap state). */
function cycleRangeAt(events: HeightEvent[], i: number): number {
  const a = events[i];
  let r = 0;
  let c = 0;
  if (events[i - 1]) {
    r += Math.abs(a.heightM - events[i - 1].heightM);
    c++;
  }
  if (events[i + 1]) {
    r += Math.abs(a.heightM - events[i + 1].heightM);
    c++;
  }
  return c ? r / c : 0;
}

/**
 * Build a secondary port from its standard (reference) port by the classic
 * differences method: shift each HW/LW time by the port's HW/LW time difference,
 * and shift its height by the height difference interpolated between the neaps
 * and springs values by where the day's range sits (springRatio). The standard
 * port's own HW/LW carry the date-to-date variation; the differences just slide
 * each one. Returns null if the reference standard port isn't loaded.
 */
function buildSecondary(id: string, def: SecondaryDef): HeightPort | null {
  const ref = HEIGHT_PORTS.find((p) => p.id === def.ref);
  if (!ref) return null;
  const events: HeightEvent[] = ref.events.map((e, i) => {
    const sr = springRatioFromRange(ref, cycleRangeAt(ref.events, i));
    const tdMin = e.type === 'H' ? def.hwTimeDiffMin : def.lwTimeDiffMin;
    const hd =
      e.type === 'H'
        ? def.hwNeapDiffM + (def.hwSpringDiffM - def.hwNeapDiffM) * sr
        : def.lwNeapDiffM + (def.lwSpringDiffM - def.lwNeapDiffM) * sr;
    return { t: e.t + tdMin * 60_000, type: e.type, heightM: Math.round((e.heightM + hd) * 100) / 100 };
  });
  let minRangeM = Infinity;
  let maxRangeM = 0;
  for (let i = 0; i + 1 < events.length; i++) {
    const r = Math.abs(events[i + 1].heightM - events[i].heightM);
    if (r < minRangeM) minRangeM = r;
    if (r > maxRangeM) maxRangeM = r;
  }
  const { curve } = curveFor(id); // no per-port curve: shares the standard shape
  return {
    id,
    name: def.name,
    tzH: def.tzH,
    events,
    minRangeM: Number.isFinite(minRangeM) ? minRangeM : 0,
    maxRangeM,
    curve,
    ownCurve: false,
    secondaryOf: ref.name,
    position: PORT_POSITIONS[id],
  };
}

// --- Direct secondary ports (real eNav data, reference port's curve) --------
// eNav publishes a full-year HW/LW feed for EVERY port, standard and secondary.
// Where we have a secondary's own feed we use it directly, which is more accurate
// than the differences method (the differences were themselves derived from these
// feeds). The tidal CURVE is borrowed from the reference standard port (Colville):
// a secondary port reads its rise and fall off its standard port's curve, the
// standard RYA practice, so ownCurve stays false and secondaryOf names the ref.
const DIRECT_SECONDARIES: { raw: RawPort; refId: string }[] = [
  { raw: edenfieldRaw as RawPort, refId: 'colville' },
  { raw: sweetwaterRaw as RawPort, refId: 'colville' },
  { raw: sandquayRaw as RawPort, refId: 'colville' },
  { raw: blackmillRaw as RawPort, refId: 'colville' },
];

function buildDirectSecondary(raw: RawPort, refId: string): HeightPort {
  const port = buildPort(raw);
  const ref = HEIGHT_PORTS.find((p) => p.id === refId);
  if (ref) {
    port.curve = ref.curve; // read rise/fall off the reference standard port's curve
    port.ownCurve = false;
    port.secondaryOf = ref.name;
  }
  return port;
}

for (const { raw, refId } of DIRECT_SECONDARIES) {
  HEIGHT_PORTS.push(buildDirectSecondary(raw, refId));
}

// Fallback: the classic differences method, kept for any secondary we DON'T hold
// direct eNav data for. With all four current secondaries captured directly this
// loop adds nothing, but it stays so a secondary without its own feed (a different
// reference port, say) can still be built from standard + differences.
for (const [id, def] of Object.entries(
  (secondariesRaw as { ports: Record<string, SecondaryDef> }).ports,
)) {
  if (HEIGHT_PORTS.some((p) => p.id === id)) continue; // already loaded directly
  const p = buildSecondary(id, def);
  if (p) HEIGHT_PORTS.push(p);
}

/**
 * A height port by id, STRICTLY: undefined when there is no such port.
 *
 * Prefer this. getHeightPort below answers an unknown id with the first port in
 * the list, which is a fictional Namley one, and that silent fall back is how a
 * fictional tide reached the depth-under-keel alarm on real charts. Anything
 * reading a tide should go through tideNow.ts, which uses this.
 */
export function findHeightPort(id: string): HeightPort | undefined {
  return HEIGHT_PORTS.find((p) => p.id === id);
}

/**
 * A height port by id, falling back to the first one in the list.
 *
 * The fall back is only safe where the caller has ALREADY established that the
 * training model applies, e.g. the training tide sheet's own port picker. For
 * reading a height anywhere else, use tideNow.ts: an unknown id here answers
 * with a fictional port whose events look entirely real.
 */
export function getHeightPort(id: string): HeightPort {
  return findHeightPort(id) ?? HEIGHT_PORTS[0];
}

/** Index i with events[i] <= ms < events[i+1], or null outside the data window. */
function bracketIndex(port: HeightPort, ms: number): number | null {
  const ev = port.events;
  if (!ev.length || ms < ev[0].t || ms > ev[ev.length - 1].t) return null;
  let lo = 0;
  let hi = ev.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ev[mid].t <= ms) lo = mid;
    else hi = mid - 1;
  }
  return lo >= ev.length - 1 ? null : lo;
}

/** The HW/LW events bracketing `ms`, or null when outside the data window. */
export function bracketing(port: HeightPort, ms: number): { prev: HeightEvent; next: HeightEvent } | null {
  const i = bracketIndex(port, ms);
  return i == null ? null : { prev: port.events[i], next: port.events[i + 1] };
}

/** Sample a digitised factor array at `hours` from HW (0.25 h steps, clamped 0..6). */
function curveFactor(arr: number[], hours: number): number {
  const step = CURVES.hoursStep;
  const h = Math.min(6, Math.max(0, Math.abs(hours)));
  const fi = h / step;
  const i = Math.floor(fi);
  if (i >= arr.length - 1) return arr[arr.length - 1];
  const u = fi - i;
  return arr[i] + (arr[i + 1] - arr[i]) * u;
}

/**
 * Spring fraction (0 = neaps, 1 = springs) for a cycle range, from the port's
 * MEAN spring and neap ranges (the diagram legend). Falls back to the year's
 * min/max range spread when no published mean ranges are available.
 */
export function springRatioFromRange(port: HeightPort, rangeM: number): number {
  const { springRangeM, neapRangeM } = port.curve;
  const denom = springRangeM - neapRangeM;
  if (denom > 0) return Math.max(0, Math.min(1, (rangeM - neapRangeM) / denom));
  const span = port.maxRangeM - port.minRangeM;
  if (span <= 0) return 0.5;
  return Math.max(0, Math.min(1, (rangeM - port.minRangeM) / span));
}

/**
 * Height of tide (metres above chart datum) at `ms`, or null outside the window,
 * by the standard Admiralty tidal-curve method. We find the bracketing HW and
 * LW, read the rise/fall factor off the port's spring and neap curves at the
 * hours-from-HW, interpolate between them by the day's range vs the port's mean
 * ranges, then height = LW + factor x (HW - LW). Exact at HW (factor 1) and LW
 * (factor ~0).
 */
export function heightAt(port: HeightPort, ms: number): number | null {
  const b = bracketing(port, ms);
  if (!b) return null;
  // One of the pair is the High Water, the other the Low Water.
  const hw = b.prev.type === 'H' ? b.prev : b.next;
  const lw = b.prev.type === 'H' ? b.next : b.prev;
  const range = hw.heightM - lw.heightM;
  if (range <= 0) return hw.heightM;
  const hoursFromHW = (ms - hw.t) / 3_600_000;
  const sr = springRatioFromRange(port, range);
  const fSpring = curveFactor(port.curve.spring, hoursFromHW);
  const fNeap = curveFactor(port.curve.neap, hoursFromHW);
  const factor = fNeap + (fSpring - fNeap) * sr;
  return lw.heightM + factor * range;
}

export type SpringLabel = 'Springs' | 'Neaps' | 'Mid';
export function springLabel(ratio: number): SpringLabel {
  if (ratio >= 0.66) return 'Springs';
  if (ratio <= 0.33) return 'Neaps';
  return 'Mid';
}

export interface TideHeightState {
  heightM: number;
  /** True when the tide is rising (the next event is a High Water). */
  rising: boolean;
  prev: HeightEvent;
  next: HeightEvent;
  springRatio: number;
  springLabel: SpringLabel;
}

/** Height + rising/falling + the bracketing HW/LW + spring/neap state at `ms`. */
export function tideHeightState(port: HeightPort, ms: number): TideHeightState | null {
  const b = bracketing(port, ms);
  if (!b) return null;
  const h = heightAt(port, ms);
  if (h == null) return null;
  const ratio = springRatioFromRange(port, Math.abs(b.next.heightM - b.prev.heightM));
  return {
    heightM: h,
    rising: b.next.type === 'H',
    prev: b.prev,
    next: b.next,
    springRatio: ratio,
    springLabel: springLabel(ratio),
  };
}

/**
 * The training port nearest a position, with the distance in nautical miles.
 * Used to order the port picker by proximity to the boat (or the chart centre),
 * and to pick the reference station for a marina that carries no tides of its
 * own. Ports with no charted position are skipped; null when none have one.
 */
export function nearestHeightPort(pos: [number, number]): { port: HeightPort; distNm: number } | null {
  let best: HeightPort | null = null;
  let bestNm = Infinity;
  for (const p of HEIGHT_PORTS) {
    if (!p.position) continue;
    const d = rhumbDistanceNm(pos, p.position);
    if (d < bestNm) {
      bestNm = d;
      best = p;
    }
  }
  return best ? { port: best, distNm: bestNm } : null;
}

/**
 * The next High or Low Water strictly after `ms`, or null when the training-year
 * data window has nothing further (we've run off the end of the almanac, or the
 * port carries no events). Used by the anchor watch to read the depth at the next
 * LW and next HW: the published HW/LW height IS the extreme, so the caller uses
 * `event.heightM` directly rather than sampling the curve at the turn.
 */
export function nextEventOfType(port: HeightPort, ms: number, type: TideType): HeightEvent | null {
  for (const e of port.events) {
    if (e.t > ms && e.type === type) return e;
  }
  return null;
}
